import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Download, TrendingUp, DollarSign, BarChart3, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
interface StockData {
  rank: number;
  name: string;
  code: string;
  currentPrice: number;
  change: number;
  changeRate: number;
  volume: number;
  tradingValue: number;
  chartLink: string;
  market: string;
}

interface AnalysisResult {
  success: boolean;
  timestamp: string;
  criteria: {
    minTradingValue: string;
    minChangeRate: string;
  };
  totalFound: number;
  kospiCount: number;
  kosdaqCount: number;
  stocks: StockData[];
}

const Index = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AnalysisResult | null>(null);
  const { toast } = useToast();

  const analyzeStocks = async () => {
    setLoading(true);
    try {
      // Supabase Edge Function을 통해 네이버 증권 실제 데이터 가져오기
      const response = await fetch('https://akcxkuucufagpgbnnvwy.supabase.co/functions/v1/debug_count_fix_2025_09_23_14_03', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFrY3hrdXVjdWZhZ3BnYm5udnd5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTg2MzQ0MTksImV4cCI6MjA3NDIxMDQxOX0.D8o_CC9FLP0LS5AZvn63f3Ry72kkotJBxTPBTJTbzXc'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        const analysisResult: AnalysisResult = {
          success: true,
          timestamp: data.timestamp,
          criteria: data.criteria,
          totalFound: data.totalFound,
          stocks: data.stocks
        };
        
        setResult(analysisResult);
        
        toast({
          title: "분석 완료",
          description: `총 ${data.totalFound}개 종목 발견 (코스피: ${data.kospiCount}개, 코스닥: ${data.kosdaqCount}개)`,
        });
      } else {
        throw new Error(data.error || '분석 중 오류가 발생했습니다.');
      }
    } catch (error: any) {
      console.error('Error analyzing stocks:', error);
      
      // 실시간 데이터 실패 시 샘플 데이터로 폴백
      const sampleStocks: StockData[] = [
        {
          rank: 1,
          name: "삼성전자",
          code: "005930",
          currentPrice: 84700,
          change: 1200,
          changeRate: 15.44,
          volume: 22655011,
          tradingValue: 191969,
          chartLink: "https://finance.naver.com/item/fchart.naver?code=005930"
        },
        {
          rank: 2,
          name: "SK하이닉스", 
          code: "000660",
          currentPrice: 145000,
          change: 15000,
          changeRate: 11.54,
          volume: 5000000,
          tradingValue: 725000,
          chartLink: "https://finance.naver.com/item/fchart.naver?code=000660"
        }
      ];

      const fallbackResult: AnalysisResult = {
        success: true,
        timestamp: new Date().toISOString(),
        criteria: {
          minTradingValue: '1,000억원',
          minChangeRate: '+5%'
        },
        totalFound: sampleStocks.length,
        kospiCount: sampleStocks.length,
        kosdaqCount: 0,
        stocks: sampleStocks
      };

      setResult(fallbackResult);
      
      toast({
        title: "샘플 데이터 로드",
        description: "실시간 데이터 로드에 실패하여 샘플 데이터를 표시합니다.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (!result || !result.stocks.length) {
      toast({
        title: "다운로드 실패",
        description: "다운로드할 데이터가 없습니다. 먼저 종목 분석을 실행해주세요.",
        variant: "destructive",
      });
      return;
    }

    const headers = ['순위', '시장', '종목명', '종목코드', '현재가', '전일비', '등락률(%)', '거래량', '거래대금(백만원)', '거래대금(억원)', '차트링크'];
    const csvContent = [
      headers.join(','),
      ...result.stocks.map((stock, index) => [
        index + 1,
        stock.market || 'KOSPI',
        `"${stock.name}"`,
        stock.code,
        stock.currentPrice.toLocaleString().replace(/,/g, ''),
        stock.change,
        stock.changeRate,
        stock.volume.toLocaleString().replace(/,/g, ''),
        stock.tradingValue.toLocaleString().replace(/,/g, ''),
        Math.round(stock.tradingValue / 100),
        stock.chartLink
      ].join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `코스피코스닥_상승종목_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "CSV 다운로드 완료",
      description: `${result.stocks.length}개 종목 데이터가 다운로드되었습니다.`,
    });
  };

  const downloadJSON = () => {
    if (!result) {
      toast({
        title: "다운로드 실패",
        description: "다운로드할 데이터가 없습니다. 먼저 종목 분석을 실행해주세요.",
        variant: "destructive",
      });
      return;
    }

    const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `코스피코스닥_상승종목_${new Date().toISOString().split('T')[0]}.json`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast({
      title: "JSON 다운로드 완료",
      description: `${result.stocks.length}개 종목 데이터가 다운로드되었습니다.`,
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-indigo-900">
      <div className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-4 flex items-center justify-center gap-3">
            <TrendingUp className="h-10 w-10" />
            코스피 & 코스닥 상승 종목 분석기
          </h1>
          <p className="text-blue-100 text-lg">
            거래대금 1,000억원 이상, 등락률 +5% 이상 종목을 실시간으로 분석합니다
          </p>
        </div>

        <div className="max-w-6xl mx-auto space-y-6">
          {/* 분석 실행 카드 */}
          <Card className="bg-white/10 backdrop-blur-sm border-white/20">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                종목 분석 실행
              </CardTitle>
              <CardDescription className="text-blue-100">
                코스피와 코스닥에서 실시간 데이터를 가져와 조건에 맞는 종목을 찾습니다
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <Button 
                  onClick={analyzeStocks} 
                  disabled={loading}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      분석 중...
                    </>
                  ) : (
                    <>
                      <BarChart3 className="mr-2 h-4 w-4" />
                      종목 분석 시작
                    </>
                  )}
                </Button>
                
                {result && (
                  <div className="flex gap-2">
                    <Button 
                      onClick={downloadCSV} 
                      variant="outline"
                      className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      CSV 다운로드
                    </Button>
                    <Button 
                      onClick={downloadJSON} 
                      variant="outline"
                      className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      JSON 다운로드
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* 분석 결과 */}
          {result && (
            <>
              {/* 요약 정보 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-white/10 backdrop-blur-sm border-white/20">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-sm">발견된 종목 수</p>
                        <p className="text-2xl font-bold text-white">{result.totalFound}개</p>
                      </div>
                      <TrendingUp className="h-8 w-8 text-green-400" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-white/10 backdrop-blur-sm border-white/20">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-sm">최소 거래대금</p>
                        <p className="text-2xl font-bold text-white">{result.criteria.minTradingValue}</p>
                      </div>
                      <DollarSign className="h-8 w-8 text-blue-400" />
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="bg-white/10 backdrop-blur-sm border-white/20">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-sm">최소 등락률</p>
                        <p className="text-2xl font-bold text-white">+5%</p>
                      </div>
                      <BarChart3 className="h-8 w-8 text-green-400" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* 종목 목록 - 탭으로 분리 */}
              <Card className="bg-white/10 backdrop-blur-sm border-white/20">
                <CardHeader>
                  <CardTitle className="text-white">분석 결과</CardTitle>
                  <CardDescription className="text-blue-100">
                    분석 시간: {new Date(result.timestamp).toLocaleString('ko-KR')} | 코스피: {result.kospiCount}개, 코스닥: {result.kosdaqCount}개
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {result.stocks.length > 0 ? (
                    <Tabs defaultValue="all" className="w-full">
                      <TabsList className="grid w-full grid-cols-4 bg-white/10">
                        <TabsTrigger value="all" className="text-white data-[state=active]:bg-blue-600">
                          전체 ({result.totalFound})
                        </TabsTrigger>
                        <TabsTrigger value="kospi" className="text-white data-[state=active]:bg-blue-600">
                          코스피 ({result.kospiCount})
                        </TabsTrigger>
                        <TabsTrigger value="kosdaq" className="text-white data-[state=active]:bg-purple-600">
                          코스닥 ({result.kosdaqCount})
                        </TabsTrigger>
                        <TabsTrigger value="top" className="text-white data-[state=active]:bg-green-600">
                          상위 10개
                        </TabsTrigger>
                      </TabsList>
                      
                      {/* 전체 종목 */}
                      <TabsContent value="all" className="mt-4">
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="border-white/20">
                                <TableHead className="text-blue-100">순위</TableHead>
                                <TableHead className="text-blue-100">시장</TableHead>
                                <TableHead className="text-blue-100">종목명</TableHead>
                                <TableHead className="text-blue-100">현재가</TableHead>
                                <TableHead className="text-blue-100">등락률</TableHead>
                                <TableHead className="text-blue-100">거래대금</TableHead>
                                <TableHead className="text-blue-100">차트</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {result.stocks.map((stock, index) => (
                                <TableRow key={stock.code} className="border-white/10">
                                  <TableCell className="text-white">{index + 1}</TableCell>
                                  <TableCell>
                                    <Badge 
                                      variant="outline" 
                                      className={stock.market === 'KOSPI' 
                                        ? "bg-blue-500/20 text-blue-300 border-blue-500/30" 
                                        : "bg-purple-500/20 text-purple-300 border-purple-500/30"
                                      }
                                    >
                                      {stock.market}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-white font-medium">{stock.name}</TableCell>
                                  <TableCell className="text-white">
                                    {stock.currentPrice.toLocaleString()}원
                                  </TableCell>
                                  <TableCell>
                                    <Badge 
                                      variant="secondary" 
                                      className="bg-green-500/20 text-green-300 border-green-500/30"
                                    >
                                      +{stock.changeRate}%
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-white">
                                    {(stock.tradingValue / 100).toLocaleString()}억원
                                  </TableCell>
                                  <TableCell>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                                      onClick={() => window.open(stock.chartLink, '_blank')}
                                    >
                                      차트 보기
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </TabsContent>

                      {/* 코스피 종목 */}
                      <TabsContent value="kospi" className="mt-4">
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="border-white/20">
                                <TableHead className="text-blue-100">순위</TableHead>
                                <TableHead className="text-blue-100">종목명</TableHead>
                                <TableHead className="text-blue-100">현재가</TableHead>
                                <TableHead className="text-blue-100">등락률</TableHead>
                                <TableHead className="text-blue-100">거래대금</TableHead>
                                <TableHead className="text-blue-100">차트</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {result.stocks
                                .filter(stock => stock.market === 'KOSPI')
                                .map((stock, index) => (
                                <TableRow key={stock.code} className="border-white/10">
                                  <TableCell className="text-white">{index + 1}</TableCell>
                                  <TableCell className="text-white font-medium">{stock.name}</TableCell>
                                  <TableCell className="text-white">
                                    {stock.currentPrice.toLocaleString()}원
                                  </TableCell>
                                  <TableCell>
                                    <Badge 
                                      variant="secondary" 
                                      className="bg-green-500/20 text-green-300 border-green-500/30"
                                    >
                                      +{stock.changeRate}%
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-white">
                                    {(stock.tradingValue / 100).toLocaleString()}억원
                                  </TableCell>
                                  <TableCell>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                                      onClick={() => window.open(stock.chartLink, '_blank')}
                                    >
                                      차트 보기
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                        {result.stocks.filter(stock => stock.market === 'KOSPI').length === 0 && (
                          <div className="text-center py-8">
                            <p className="text-blue-100">조건에 맞는 코스피 종목이 없습니다.</p>
                          </div>
                        )}
                      </TabsContent>

                      {/* 코스닥 종목 */}
                      <TabsContent value="kosdaq" className="mt-4">
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="border-white/20">
                                <TableHead className="text-blue-100">순위</TableHead>
                                <TableHead className="text-blue-100">종목명</TableHead>
                                <TableHead className="text-blue-100">현재가</TableHead>
                                <TableHead className="text-blue-100">등락률</TableHead>
                                <TableHead className="text-blue-100">거래대금</TableHead>
                                <TableHead className="text-blue-100">차트</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {result.stocks
                                .filter(stock => stock.market === 'KOSDAQ')
                                .map((stock, index) => (
                                <TableRow key={stock.code} className="border-white/10">
                                  <TableCell className="text-white">{index + 1}</TableCell>
                                  <TableCell className="text-white font-medium">{stock.name}</TableCell>
                                  <TableCell className="text-white">
                                    {stock.currentPrice.toLocaleString()}원
                                  </TableCell>
                                  <TableCell>
                                    <Badge 
                                      variant="secondary" 
                                      className="bg-green-500/20 text-green-300 border-green-500/30"
                                    >
                                      +{stock.changeRate}%
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-white">
                                    {(stock.tradingValue / 100).toLocaleString()}억원
                                  </TableCell>
                                  <TableCell>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                                      onClick={() => window.open(stock.chartLink, '_blank')}
                                    >
                                      차트 보기
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                        {result.stocks.filter(stock => stock.market === 'KOSDAQ').length === 0 && (
                          <div className="text-center py-8">
                            <p className="text-blue-100">조건에 맞는 코스닥 종목이 없습니다.</p>
                          </div>
                        )}
                      </TabsContent>

                      {/* 상위 10개 종목 */}
                      <TabsContent value="top" className="mt-4">
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow className="border-white/20">
                                <TableHead className="text-blue-100">순위</TableHead>
                                <TableHead className="text-blue-100">시장</TableHead>
                                <TableHead className="text-blue-100">종목명</TableHead>
                                <TableHead className="text-blue-100">현재가</TableHead>
                                <TableHead className="text-blue-100">등락률</TableHead>
                                <TableHead className="text-blue-100">거래대금</TableHead>
                                <TableHead className="text-blue-100">차트</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {result.stocks.slice(0, 10).map((stock, index) => (
                                <TableRow key={stock.code} className="border-white/10">
                                  <TableCell className="text-white">{index + 1}</TableCell>
                                  <TableCell>
                                    <Badge 
                                      variant="outline" 
                                      className={stock.market === 'KOSPI' 
                                        ? "bg-blue-500/20 text-blue-300 border-blue-500/30" 
                                        : "bg-purple-500/20 text-purple-300 border-purple-500/30"
                                      }
                                    >
                                      {stock.market}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-white font-medium">{stock.name}</TableCell>
                                  <TableCell className="text-white">
                                    {stock.currentPrice.toLocaleString()}원
                                  </TableCell>
                                  <TableCell>
                                    <Badge 
                                      variant="secondary" 
                                      className="bg-green-500/20 text-green-300 border-green-500/30"
                                    >
                                      +{stock.changeRate}%
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-white">
                                    {(stock.tradingValue / 100).toLocaleString()}억원
                                  </TableCell>
                                  <TableCell>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                                      onClick={() => window.open(stock.chartLink, '_blank')}
                                    >
                                      차트 보기
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </TabsContent>
                    </Tabs>
                  ) : (
                    <div className="text-center py-8">
                      <p className="text-blue-100">조건에 맞는 종목이 없습니다.</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Index;